import classNames from "classnames";
import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { postChangeAccountDefaultOrgId } from "../../../../api/accounts";
import { OrganizationsItemRaw } from "../../../../domain/Organization";
import { useAppAccount } from "../../../AppProvider/AppAccountProvider";
import { useAppNotificationManager } from "../../../AppProvider/AppNotificationProvider";
import { useAppOrgsAndInvitedOrgs } from "../../../AppProvider/AppOrgsAndInvitedOrgsProvider";
import { useAppSideNavOpenState } from "../../providers/AppSideNavOpenStateProvider";

import styles from "./index.module.css";

import { useAppRouteParams } from "@/AppRoutes";
import { translation } from "@/i18n";
import { nsOrg } from "@/i18n/locales/org";
import { buildOrgTopPath } from "@/utils/orgTopPath";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Tooltip,
} from "@fluentui/react-components";
import { WrenchScrewdriverRegular } from "@fluentui/react-icons";

const useTranslation = translation.use(nsOrg);

export const OrganizationSwitchDialog: React.FC<{
  isVisibleDialog: boolean;
  closeDialog: () => void;
}> = ({ isVisibleDialog, closeDialog }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { account, setAccount, isAAAdminOrAbove } = useAppAccount();
  const { organizationId } = useAppRouteParams();
  const { organizations } = useAppOrgsAndInvitedOrgs();
  const { showErrorNotification } = useAppNotificationManager();
  const { openGlobalForOrg } = useAppSideNavOpenState();

  const [isInProgress, setIsInProgress] = useState(false);

  const getIsDisableLink = useCallback(
    (targetOrganization: OrganizationsItemRaw) =>
      targetOrganization.org_id === organizationId || isInProgress,
    [organizationId, isInProgress]
  );

  const runChangeOrganization = useCallback(
    async (targetOrganization: OrganizationsItemRaw) => {
      // 対象組織が遷移可能か
      if (getIsDisableLink(targetOrganization)) return;
      try {
        setIsInProgress(true);
        const response = await postChangeAccountDefaultOrgId({
          uid: account?.uid ?? "",
          default_org_id: targetOrganization.org_id,
        });
        setAccount(response);
        setIsInProgress(false);
      } catch (error) {
        showErrorNotification(t.org.table.defaultOrgChangeError(), error);
      }
      closeDialog();
      // 組織切り替え時は遷移先組織でグローバルナビを開く
      if (targetOrganization.org_id) {
        openGlobalForOrg(targetOrganization.org_id);
      }
      navigate(
        targetOrganization.org_id
          ? buildOrgTopPath(
              targetOrganization.org_id,
              targetOrganization.initial_extra_page_id
            )
          : "/"
      );
    },
    [t, account, getIsDisableLink, openGlobalForOrg]
  );

  return (
    <Dialog
      open={isVisibleDialog}
      onOpenChange={() => {
        if (isVisibleDialog ?? isInProgress) return;
        closeDialog();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t.org.switchDialog.title()}</DialogTitle>
          <DialogContent>
            {organizations.length > 0
              ? t.org.switchDialog.description()
              : t.org.switchDialog.noOrganizations()}
            {organizations.length > 0 ? (
              <>
                <div className={styles.container}>
                  {[...organizations]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((targetOrganization) => {
                      return (
                        <div
                          key={targetOrganization.org_id}
                          className={classNames(styles.organizationLink, {
                            [styles.organizationDisableLink]:
                              getIsDisableLink(targetOrganization),
                          })}
                          onMouseDown={async () => {
                            await runChangeOrganization(targetOrganization);
                          }}
                        >
                          <div
                            className={classNames(styles.organizationLinkText)}
                          >
                            {targetOrganization.name} (ID :{" "}
                            {targetOrganization.org_id})
                          </div>
                          {targetOrganization.service_status ===
                          "under_maintenance" ? (
                            <Tooltip
                              content={t.org.switchDialog.underMaintenance()}
                              relationship="description"
                              withArrow={true}
                            >
                              <div
                                className={classNames(
                                  styles.organizationLinkIcon
                                )}
                              >
                                <WrenchScrewdriverRegular />
                              </div>
                            </Tooltip>
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              disabled={isInProgress}
              onClick={() => {
                closeDialog();
              }}
            >
              {t.org.switchDialog.cancelButton()}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
